<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { notifyContentChanged, reloadOnContentChanged, trackEditSession } from '@/composables/useContentRefresh';

const { t } = useI18n();

// Types
interface Pathway {
  id: number;
  title: string;
  url: string;
  icon_url: string | null;
  weight?: number | null;
  moderation_state?: string;
}

// Props from drupalSettings
const settings = (window as any).drupalSettings?.epbPathwaysBlock || {};

const collectionId = ref<number>(settings.collectionId || 0);
const pathwayId = ref<number | null>(settings.pathwayId || null);
const activePathwayData = ref<Pathway | null>(settings.activePathway || null);
const pathways = ref<Pathway[]>(settings.pathways || []);
const defaultPathwayId = ref<number | null>(settings.defaultPathwayId || null);
const pathwaysPerColumn = ref<number>(settings.pathwaysPerColumn || 15);
const pathwayTitleOverride = ref<string | null>(settings.pathwayTitleOverride || null);
const canEditOrder = ref<boolean>(settings.canEditOrder || false);
const currentGroupId = ref<number | null>(settings.currentGroupId || null);

// State
// Collapsed if a non-default pathway is selected
const collapsed = ref(!!pathwayId.value && pathwayId.value !== defaultPathwayId.value);
const editMode = ref(false);
const savingOrder = ref(false);
const orderSaveError = ref<string | null>(null);

// Drag and drop state
const draggingPathway = ref<Pathway | null>(null);
const draggingIndex = ref<number | null>(null);
const dragOverIndex = ref<number | null>(null);

// Computed
const filteredPathways = computed(() => pathways.value);
// Show with >= 1 non-default pathway (or one already selected), aligned with the
// AreasBlock awaiting_pathway gate and the PathwaysBlock PHP threshold. A single
// pathway is a valid choice ("default view" + that pathway); requiring >= 2 left
// the page blank when a collection had exactly one pathway.
const shouldShowBlock = computed(() => filteredPathways.value.length >= 1 || !!activePathwayData.value);
const activePathway = computed(() => activePathwayData.value);

// The custom title label (e.g., "Pathway" or "Parcours")
const customLabel = computed(() => pathwayTitleOverride.value || t('epb.areas.choose_pathway'));

// Is the current pathway the default one (or none selected)?
const isDefaultPathway = computed(() => {
  if (!pathwayId.value) return true;
  return pathwayId.value === defaultPathwayId.value;
});

// Build pathway URL with context
// Uses the pathway's own URL (pointing to the pathway node) and adds fa=1
function buildPathwayUrl(pathway: Pathway): string {
  // pathway.url from backend is like "/node/123?fc=216&fp=123"
  const url = new URL(pathway.url, window.location.origin);
  url.searchParams.set('fa', '1');
  return url.toString();
}

// Toggle block collapse
function toggleBlock() {
  collapsed.value = !collapsed.value;
}

// Toggle edit mode
function toggleEditMode() {
  editMode.value = !editMode.value;
  orderSaveError.value = null;
}

// Hold off sibling-block reloads while this block's drag-sort is in progress.
trackEditSession('pathwaysblock', editMode);

// Drag and drop handlers
function onDragStart(event: DragEvent, pathway: Pathway, index: number) {
  if (!editMode.value) return;
  draggingPathway.value = pathway;
  draggingIndex.value = index;
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(pathway.id));
  }
}

function onDragEnd() {
  draggingPathway.value = null;
  draggingIndex.value = null;
  dragOverIndex.value = null;
}

function onDragOver(event: DragEvent, index: number) {
  if (!editMode.value || draggingPathway.value === null) return;
  event.preventDefault();
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = 'move';
  }
  dragOverIndex.value = index;
}

function onDragLeave() {
  dragOverIndex.value = null;
}

function onDrop(event: DragEvent, targetIndex: number) {
  if (!editMode.value || !draggingPathway.value || draggingIndex.value === null) return;
  event.preventDefault();

  const sourceIndex = draggingIndex.value;
  if (sourceIndex === targetIndex) {
    onDragEnd();
    return;
  }

  const list = [...pathways.value];
  const [movedPathway] = list.splice(sourceIndex, 1);
  list.splice(targetIndex > sourceIndex ? targetIndex - 1 : targetIndex, 0, movedPathway);
  pathways.value = list;

  saveOrder(list.map(p => p.id));
  onDragEnd();
}

// Save order to backend
async function saveOrder(pathwayIds: number[]) {
  savingOrder.value = true;
  orderSaveError.value = null;

  try {
    const response = await fetch('/epbapi/areas-block/pathways-order', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        collection_id: collectionId.value,
        pathway_ids: pathwayIds,
        group_id: currentGroupId.value,
      }),
    });

    const data = await response.json();
    if (!data.success) {
      orderSaveError.value = data.error || 'Failed to save order';
    } else {
      // The reorder persisted: tell every other navigation block to refresh.
      notifyContentChanged('pathwaysblock');
    }
  } catch (e: any) {
    orderSaveError.value = e.message || 'Network error';
  } finally {
    savingOrder.value = false;
  }
}

// Navigate to pathway
function selectPathway(pathway: Pathway) {
  if (editMode.value) return;
  window.location.href = buildPathwayUrl(pathway);
}

// A pathway reorder is rendered from drupalSettings (no re-fetch endpoint), so
// reflect changes made by another navigation block with a deferred page reload.
reloadOnContentChanged('pathwaysblock');

onMounted(() => {
  // Collapsed if a non-default pathway is selected
  collapsed.value = !!pathwayId.value && pathwayId.value !== defaultPathwayId.value;
});
</script>

<template>
  <div v-if="shouldShowBlock" class="pathway-container">
    <div class="collapsible-block">
      <!-- Header / Toggle Button -->
      <button
        class="collapsible-toggle pathway-toggle"
        :aria-expanded="!collapsed"
        @click="toggleBlock"
        type="button"
      >
        <span class="collapsible-title">
          <i class="bi bi-mortarboard pe-1"></i>
          <template v-if="!isDefaultPathway && activePathway">
            {{ customLabel }} - <span
              class="current-pathway-title"
              :class="activePathway.moderation_state ? `moderation_state-${activePathway.moderation_state}` : ''"
            >{{ activePathway.title }}</span>
          </template>
          <template v-else>
            {{ customLabel }}
          </template>

          <!-- Edit order button -->
          <a
            v-if="canEditOrder"
            class="epb-order-icon"
            href="#"
            :title="editMode ? 'Exit edit mode' : 'Order Pathways'"
            @click.stop.prevent="toggleEditMode"
          >
            <i class="bi" :class="editMode ? 'bi-check-lg' : 'bi-sort-down'"></i>
          </a>

          <!-- Saving indicator -->
          <span v-if="savingOrder" class="ms-1">
            <span class="spinner-border spinner-border-sm" role="status"></span>
          </span>

          <!-- Error indicator -->
          <span v-if="orderSaveError" class="ms-1 text-danger small" :title="orderSaveError">
            <i class="bi bi-exclamation-triangle"></i>
          </span>
        </span>

        <span class="collapse-icon">
          <i class="bi bi-chevron-compact-down collapsed-icon" :class="{ 'd-none': !collapsed }"></i>
          <i class="bi bi-chevron-compact-up expended-icon" :class="{ 'd-none': collapsed }"></i>
        </span>
      </button>

      <!-- Content area -->
      <div
        class="collapsible-content pathway-content"
        :class="{ collapsed }"
      >
        <div class="block-content">
          <ul class="epb-unformatted-list" :class="{ 'edit-mode': editMode }">
            <li
              v-for="(pathway, index) in filteredPathways"
              :key="pathway.id"
              :class="{
                'draggable': editMode,
                'dragging': draggingPathway?.id === pathway.id,
                'drag-over': dragOverIndex === index && draggingPathway !== null
              }"
              :draggable="editMode"
              @dragstart="onDragStart($event, pathway, index)"
              @dragend="onDragEnd"
              @dragover="onDragOver($event, index)"
              @dragleave="onDragLeave"
              @drop="onDrop($event, index)"
            >
              <span v-if="editMode" class="drag-handle me-2">
                <i class="bi bi-grip-vertical"></i>
              </span>
              <a
                :href="buildPathwayUrl(pathway)"
                :class="[
                  { 'active': pathway.id === pathwayId },
                  pathway.moderation_state ? `moderation_state-${pathway.moderation_state}` : ''
                ]"
                @click.prevent="selectPathway(pathway)"
              >
                <span v-if="pathway.icon_url" class="pathway-icon me-2">
                  <img :src="pathway.icon_url" alt="" class="pathway-icon-img">
                </span>
                {{ pathway.title }}
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Minimal scoped styles - main styles come from pathway.css in theme */

.collapsible-content.collapsed {
  display: none;
}

/* Remove bullet points and add spacing */
.epb-unformatted-list {
  list-style: none;
  padding: 0.5rem 1rem;
  margin: 0;
}

.epb-unformatted-list li {
  padding: 0.25rem 0;
}

/* Edit mode / Drag and drop */
.epb-unformatted-list.edit-mode {
  border: 2px dashed var(--bs-warning);
  border-radius: 4px;
  padding: 0.5rem;
  background-color: var(--bs-warning-bg-subtle);
}

.epb-unformatted-list li.draggable {
  cursor: grab;
  user-select: none;
  display: flex;
  align-items: center;
  padding: 0.5rem;
  margin: 2px 0;
  background-color: var(--bs-body-bg);
  border: 1px solid var(--bs-gray-300);
  border-radius: 4px;
}

.epb-unformatted-list li.dragging {
  opacity: 0.5;
  cursor: grabbing;
}

.epb-unformatted-list li.drag-over {
  border-top: 3px solid var(--bs-primary);
  margin-top: -1px;
}

.drag-handle {
  color: var(--bs-gray-500);
  cursor: grab;
}

.drag-handle:hover {
  color: var(--bs-gray-700);
}

.pathway-icon-img {
  width: 16px;
  height: 16px;
  object-fit: contain;
  vertical-align: middle;
}

.epb-order-icon {
  margin-left: 0.5rem;
  color: var(--bs-gray-600);
}

.epb-order-icon:hover {
  color: var(--bs-primary);
}

/* All pathways in bold black */
.epb-unformatted-list a {
  font-weight: bold;
  color: var(--bs-gray-700);
  text-decoration: none;
}

/* Active pathway in primary color */
.epb-unformatted-list a.active {
  color: var(--bs-primary);
}
</style>
